// frontend/src/components/vacancy/VacancyCard.tsx

import React from 'react'
import { Link } from 'react-router-dom'
import { formatDate, formatCurrency, getStatusColor, getStatusLabel, truncateText } from '@/utils/helpers'
import { Card } from '@/components/common/UI/Card'
import { Badge } from '@/components/common/UI/Badge'
import { Button } from '@/components/common/UI/Button'
import { useAuthStore } from '@/store'

interface VacancyCardProps {
  vacancy: any
  compact?: boolean
  onApply?: () => void
  actions?: React.ReactNode
}

export const VacancyCard: React.FC<VacancyCardProps> = ({
  vacancy,
  compact = false,
  onApply,
  actions
}) => {
  const { user, isAuthenticated } = useAuthStore()
  const isEmployer = user?.role === 'employer' || user?.role === 'admin'

  if (compact) {
    return (
      <Card className="p-4 hover:shadow-md transition-shadow">
        <Link to={`/vacancies/${vacancy.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-secondary-900 hover:text-primary-600 transition-colors truncate">
                {vacancy.title}
              </h4>
              <p className="text-sm text-secondary-600 truncate">
                {vacancy.company?.name || vacancy.company_name}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-secondary-400">
                <span>{vacancy.city || 'Не указан'}</span>
                {vacancy.salary_from && (
                  <span>
                    {formatCurrency(vacancy.salary_from, vacancy.salary_currency)}
                    {vacancy.salary_to && ` - ${formatCurrency(vacancy.salary_to, vacancy.salary_currency)}`}
                  </span>
                )}
              </div>
            </div>
            <div className="ml-4 flex flex-col items-end gap-1">
              <Badge className={getStatusColor(vacancy.moderation_status)}>
                {getStatusLabel(vacancy.moderation_status)}
              </Badge>
              <span className="text-xs text-secondary-400">
                {formatDate(vacancy.created_at)}
              </span>
            </div>
          </div>
        </Link>
      </Card>
    )
  }

  return (
      <Card className="group overflow-hidden border border-secondary-200 bg-white p-0 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg">
      <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          {/* Заголовок */}
          <Link to={`/vacancies/${vacancy.id}`}>
            <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-secondary-900 transition-colors group-hover:text-primary-700 sm:text-xl">
              {vacancy.title}
            </h3>
          </Link>

          {/* Компания */}
          {(vacancy.company?.id || vacancy.company_id) ? (
            <Link
              to={`/companies/${vacancy.company?.id || vacancy.company_id}`}
              className="mt-1 inline-block text-sm font-medium text-secondary-600 transition-colors hover:text-primary-700"
            >
              {vacancy.company?.name || vacancy.company_name}
            </Link>
          ) : (
            <span className="mt-1 inline-block text-sm font-medium text-secondary-600">{vacancy.company?.name || vacancy.company_name}</span>
          )}

          {/* Детали */}
          <div className="mt-4 flex flex-wrap gap-2 text-sm text-secondary-700">
            {vacancy.city && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-3 py-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {vacancy.city}
              </span>
            )}
            {vacancy.salary_display ? (
              <span className="rounded-full bg-green-50 px-3 py-1.5 font-semibold text-green-800">{vacancy.salary_display}</span>
            ) : vacancy.salary_from ? (
              <span className="rounded-full bg-green-50 px-3 py-1.5 font-semibold text-green-800">
                {formatCurrency(vacancy.salary_from, vacancy.salary_currency)}
                {vacancy.salary_to && ` - ${formatCurrency(vacancy.salary_to, vacancy.salary_currency)}`}
              </span>
            ) : null}
            {vacancy.remote_available && (
              <span className="rounded-full bg-primary-50 px-3 py-1.5 text-primary-700">🌐 Удаленно</span>
            )}
            {vacancy.experience_level && (
              <span className="rounded-full bg-secondary-50 px-3 py-1.5 text-secondary-600">
                Опыт: {vacancy.experience_level.replace('_', ' ')}
              </span>
            )}
          </div>

          {/* Навыки */}
          {vacancy.key_skills && vacancy.key_skills.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {vacancy.key_skills.slice(0, 5).map((skill: string) => (
                <span
                  key={skill}
                  className="rounded-full border border-primary-100 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700"
                >
                  {skill}
                </span>
              ))}
              {vacancy.key_skills.length > 5 && (
                <span className="text-xs text-secondary-400">
                  +{vacancy.key_skills.length - 5}
                </span>
              )}
            </div>
          )}

          {/* Описание */}
          <p className="mt-4 line-clamp-3 text-sm leading-6 text-secondary-600">
            {truncateText(vacancy.description, 200)}
          </p>
        </div>
        {actions && <div className="flex flex-wrap gap-2 lg:order-3 lg:w-36 lg:flex-col">{actions}</div>}

        {/* Правая часть */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-secondary-100 pt-4 lg:order-2 lg:min-w-36 lg:flex-col lg:items-end lg:border-0 lg:pt-0">
          <Badge className={getStatusColor(vacancy.moderation_status)}>
            {getStatusLabel(vacancy.moderation_status)}
          </Badge>
          <span className="text-xs text-secondary-400">
            {formatDate(vacancy.created_at)}
          </span>
          {!isEmployer && isAuthenticated && (
            <Button size="sm" onClick={onApply}>
              Откликнуться
            </Button>
          )}
          {vacancy.is_expired && (
              <span className="text-xs font-medium text-error">Вакансия истекла</span>
          )}
        </div>
      </div>
    </Card>
  )
}
